import axios from "axios";
import wallet from "@/store/wallet";
import i18n from "@/i18n";
import { getCurrentLang } from "@/utils/lang";

// ✅ 用你刚刚封装的顶置 Toast（axios 里不能用 this.$toast）
import { topToast } from "@/plugins/topToast";

// 后端单体 admin 前缀（你的 curl 走的是 /admin）
const http = axios.create({
   baseURL: "https://dap.webbch.com/api/admin",
  // baseURL: '/api/admin',

  timeout: 15000,
});

// 每次请求可传这些“提示控制”参数
function pickFlags(config = {}) {
  return {
    silent: config.silent === true, // 静默：不提示错误
    successMsg: config.successMsg || null, // 成功提示文案（建议外面传已翻译好的）
    errorMsg: config.errorMsg || null, // 自定义失败提示文案
    // showLoading / loadingMsg 先保留，但 TopToast 没有 loading，这里不会展示
    showLoading: config.showLoading === true,
    loadingMsg: config.loadingMsg || null,
  };
}

function t(path) {
  return i18n.t(path);
}

// ✅ 统一提示出口：全部走 topToast（与 main.js 注入的样式一致）
const Tip = {
  success(msg) {
    if (!msg) return;
    topToast.success(typeof msg === "string" ? msg : msg);
  },
  warn(msg) {
    if (!msg) return;
    topToast.warn(typeof msg === "string" ? msg : msg);
  },
  error(msg) {
    if (!msg) return;
    topToast.error(typeof msg === "string" ? msg : msg);
  },
  info(msg) {
    if (!msg) return;
    topToast.show(typeof msg === "string" ? msg : msg);
  },
};

// 请求拦截：自动带 token + 语言
http.interceptors.request.use((config) => {
  const token = wallet.state?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers.Accept = "application/json";

  const lang = getCurrentLang();
  config.headers["Accept-Language"] = lang;

  return config;
});

// 响应拦截：统一解包 + 提示
http.interceptors.response.use(
  (resp) => {
    const flags = pickFlags(resp.config);
    const payload = resp?.data;

    // 标准返回 {code,msg,data}
    if (payload && typeof payload === "object" && "code" in payload) {
      if (payload.code === 0) {
        if (flags.successMsg && !flags.silent) Tip.success(flags.successMsg);
        return payload.data;
      } else {
        const fallbackMsg = t("http.requestFailed");
        const msg = flags.errorMsg || payload.msg || fallbackMsg;
        if (!flags.silent) Tip.error(msg);
        const err = new Error(msg);
        err.code = payload.code;
        throw err;
      }
    }

    // 非标准场景，直接返回原数据
    if (flags.successMsg && !flags.silent) Tip.success(flags.successMsg);
    return payload ?? resp;
  },
  (error) => {
    const flags = pickFlags(error?.config);
    const status = error?.response?.status;

    let defaultMsg;
    if (status === 401) {
      defaultMsg = t("http.unauthorized");
    } else if (status === 403) {
      defaultMsg = t("http.forbidden");
    } else if (status === 404) {
      defaultMsg = t("http.notFound");
    } else if (error?.message?.includes("timeout")) {
      defaultMsg = t("http.timeout");
    } else {
      defaultMsg = t("http.networkError");
    }

    const msg = flags.errorMsg || error?.response?.data?.msg || defaultMsg;
    if (!flags.silent) Tip.error(msg);

    if (status === 401) {
      // 统一过期处理：清状态并打开你的连接钱包弹窗
      wallet.state.token = "";
      wallet.state.connected = false;
      wallet.state.showModal = true;
    }

    return Promise.reject(error);
  }
);

// 便捷方法：支持 flags
export const apiGet = (url, params = {}, config = {}) =>
  http.get(url, { params, ...config });

export const apiPost = (url, data = {}, config = {}) =>
  http.post(url, data, { ...config });

export default http;
